/* eslint-disable no-self-assign */
import { SubjectModelSchema } from "../_offline/models/subjectModel.schema";
import { GUID, REGULAR_EXPRESSION } from "../app.constants";
import { FormatDate } from "./format-date";

export class SubjectModelNew {
  name: string;
  locations: SubjectLocation[];
  schemaVersion: string;
  isValid: boolean;
  inspectionType: number;
}

export class SubjectModelWrapper {
  subjectModel: SubjectModelListItem;
  order: number;
  constructor(subjectModel: SubjectModelListItem) {
    this.subjectModel = subjectModel;
  }
}

export class SubjectModelUpdate extends SubjectModelNew {
  id: string;
}

export class SubjectModel extends SubjectModelUpdate {
  structImagePath: string;
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;
  hasInspections: boolean;
  isNewestVersion: boolean;
  version: number;
  isOfflineData: boolean;

  setSubjectModelSchema(subjectModelSchema: SubjectModelSchema, version?: number) {
    const newestLocations = version
      ? subjectModelSchema.subjectModelLocations.find((_) => version === _.version)
      : subjectModelSchema.subjectModelLocations.find((_) => _.isNewestVersion);

    this.id = subjectModelSchema.id;
    this.name = subjectModelSchema.name;
    this.structImagePath = subjectModelSchema.structImageFilePath;
    this.lastUpdateDate = subjectModelSchema.lastUpdateDate;
    this.isValid = subjectModelSchema.isValid;

    this.locations = JSON.parse(newestLocations.locations) as SubjectLocation[];
    this.setFolderNameOfLocations(this.locations, 1);

    this.version = newestLocations.version;
    this.isOfflineData = subjectModelSchema.isOfflineData;

    this.schemaVersion = this.schemaVersion;
    this.createdBy = this.createdBy;
    this.createDate = this.createDate;
    this.lastUpdatedBy = this.lastUpdatedBy;
    this.hasInspections = this.hasInspections;
    this.inspectionType = this.inspectionType;
  }

  setFolderNameOfLocations(locations: SubjectLocation[], index: number): number {
    for (const location of locations) {
      if (location.children.length === 0) {
        const num = location.isDataPlate ? "00" : (index++).toString().padStart(2, "0");
        location.folderName = `${num}_${location.identifier}`;
      } else {
        index = this.setFolderNameOfLocations(location.children, index);
      }
    }
    return index;
  }
}

export class SubjectLocation {
  identifier: string;
  name: string;
  abbreviation: string;
  children: SubjectLocation[];
  rectInStructImage: RectInStructImage | null;
  description: string;
  inspectionFor: string[];
  isDataPlate: boolean;
  folderName: string;
  isOperational?: boolean = true;
  structImagePath?: string;
  assetTypeId?: string;
  liveAssetId?: string;
}

export class SubjectModelLocations {
  subjectModelId: string;
  locations: SubjectLocation[];
  version: number;
  isNewestVersion: boolean;
}

export class RectInStructImage {
  top: number;
  left: number;
  width: number;
  height: number;
}

export class SubjectModelListItem {
  id: string;
  name: string;
  isValid: boolean;
  lastUpdateDate: string;
  version: number;
}

export class SubjectModelClone {
  name: string;
  id: string;
}

export class SubjectModelDetail extends SubjectModel {
  isDeleted: boolean;

  /**
   * 使用可能文字列かどうかを判定する。
   *
   * @param value 確認対象文字列。
   * @returns 使用可能文字列の場合は true を返す。
   */
  private isAvailableCharacters(value: string): boolean {
    const availableCharacters: RegExp = new RegExp(
      [
        "^",
        REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
        "[",
        REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
        REGULAR_EXPRESSION.KANJI_PATTERN,
        REGULAR_EXPRESSION.SYMBOL_PATTERN,
        "]+$"
      ].join(""),
      "i"
    );

    return availableCharacters.test(value);
  }

  /**
   * JSON データから SubjectLocation の配列を生成して返す。
   * 項目チェックに不備があれば例外を返す。
   *
   * @param jsonValue JSON データ。
   * @returns Array<SubjectLocation>
   */
  private getSubjectLocations(jsonValue: any): Array<SubjectLocation> {
    if (!jsonValue) {
      return new Array<SubjectLocation>();
    }

    const result: Array<SubjectLocation> = new Array<SubjectLocation>();

    for (const jsonLocation of jsonValue) {
      // 必須チェック
      if (!jsonLocation?.identifier) {
        throw new Error(`SubjectModelLocation's identifier is required.`);
      }
      if (!jsonLocation?.name) {
        throw new Error(`SubjectModelLocation's name is required.`);
      }
      if (!jsonLocation?.abbreviation) {
        throw new Error(`SubjectModelLocation's abbreviation is required.`);
      }

      const location: SubjectLocation = new SubjectLocation();

      location.identifier = jsonLocation.identifier;
      location.name = jsonLocation.name;
      location.abbreviation = jsonLocation.abbreviation;
      location.description = jsonLocation?.description ?? null;
      location.inspectionFor = jsonLocation?.inspectionFor ?? new Array<string>();
      location.isDataPlate = jsonLocation?.isDataPlate ?? false;
      location.rectInStructImage = new RectInStructImage();
      location.rectInStructImage.top = jsonLocation?.rectInStructImage?.top ?? null;
      location.rectInStructImage.left = jsonLocation?.rectInStructImage?.left ?? null;
      location.rectInStructImage.width = jsonLocation?.rectInStructImage?.width ?? null;
      location.rectInStructImage.height = jsonLocation?.rectInStructImage?.height ?? null;
      location.structImagePath = jsonLocation?.structImagePath ?? null;
      location.children = !jsonLocation?.children ? new Array<SubjectLocation>() : this.getSubjectLocations(jsonLocation.children);
      if (jsonLocation?.folderName !== undefined) {
        // Backend で設定されている Newtonsoft.Json.NullValueHandling.Ignore に合わせて undefined では無い場合に設定する。
        location.folderName = jsonLocation.folderName;
      }

      result.push(location);
    }

    return result;
  }

  /**
   * 対象項目に半角スペース、全角スペースが含まれるかどうかを返す。
   *
   * @param locations SubjectLocation の配列。
   * @returns 対象項目に半角スペース、全角スペースが含まれる場合は true を返す。
   */
  private containsSpace(locations: Array<SubjectLocation>): boolean {
    if (!locations) {
      return false;
    }

    for (const location of locations) {
      const regExp: RegExp = new RegExp("[ 　]");

      if (regExp.test(location.abbreviation) || regExp.test(location.identifier)) {
        return true;
      }

      if (!!location.children && location.children.length > 0) {
        if (this.containsSpace(location.children)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * 対象項目に .(dot)が含まれるかどうかを返す。
   *
   * @param locations SubjectLocation の配列。
   * @returns 対象項目に .(dot)が含まれる場合は true を返す。
   */
  private containsDot(locations: Array<SubjectLocation>): boolean {
    if (!locations) {
      return false;
    }

    for (const location of locations) {
      const regExp: RegExp = new RegExp("\\.");

      if (regExp.test(location.abbreviation) || regExp.test(location.identifier)) {
        return true;
      }

      if (!!location.children && location.children.length > 0) {
        if (this.containsDot(location.children)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * JSON オブジェクトからデータを取得、設定する。
   * Backend の SubjectModelManagementService.ImportSubjectModelAsync() を参考に実装。
   *
   * @param jsonValue JSON オブジェクト。
   * @returns エラー文言。正常時は空文字を返す。
   */
  public setValueFromJsonObject(jsonValue: any): string {
    // 項目チェック
    // ・空白チェック
    // ・必須チェック
    // ・文字列長チェック
    // ・禁止文字チェック(ProhibitCharactersAttribute)
    // ・要素数チェック(RequiredElementAttribute)

    // 必須チェック
    if (!jsonValue?.name) {
      return "The name property is required.";
    }
    if (!jsonValue?.locations) {
      return "The locations property is no less than 1 elements.";
    }
    // if (!jsonValue?.schemaVersion) {
    //   // TODO: SubjectModelSchemaにないため、一時的な処置
    //   return 'The schemaVersion property is required.';
    // }
    // if (!jsonValue?.isValid) {
    //   // true に書き換えるため不要。
    //   return 'The isValid property is required.';
    // }
    // if (!jsonValue?.inspectionType) {
    //   // TODO: SubjectModelSchemaにないため、一時的な処置
    //   return 'The inspectionType property is required.';
    // }

    this.name = jsonValue.name;
    try {
      this.locations = this.getSubjectLocations(jsonValue.locations);
    } catch (error) {
      if (error instanceof Error) {
        return error.message;
      }

      return error;
    }
    this.isValid = true;
    this.id = GUID.generateUUID();
    this.structImagePath = "";
    this.lastUpdateDate = new FormatDate().toString();
    this.isNewestVersion = true;
    this.version = 1;
    this.isDeleted = false;
    this.isOfflineData = true;

    /** SubjectModel.name の最大文字列長 */
    const SUBJECT_MODEL_NAME_MAX_LENGTH: number = 100;
    // 文字列長チェック
    if (this.name.length > SUBJECT_MODEL_NAME_MAX_LENGTH) {
      return `name is no more than ${SUBJECT_MODEL_NAME_MAX_LENGTH} characters.`;
    }
    // 禁止文字(使用可能文字)チェック
    if (!this.isAvailableCharacters(this.name)) {
      return "name includes prohibit characters or reserved word.";
    }

    // 要素数チェック(RequiredElementAttribute)
    if (!this.locations || this.locations.length == 0) {
      return "This import file is invalid.";
    }

    // 空白、 .(dot) のチェック
    if (this.containsSpace(this.locations) || this.containsDot(this.locations)) {
      return "This import file is invalid.";
    }

    this.schemaVersion = this.schemaVersion;
    this.inspectionType = this.inspectionType;
    this.createdBy = this.createdBy;
    this.createDate = this.createDate;
    this.lastUpdatedBy = this.lastUpdatedBy;
    this.hasInspections = this.hasInspections;

    return "";
  }
}
